import React, { useEffect, useState } from 'react';
import { Search } from 'lucide-react';

import SprintModal from '../Sprint/SprintModal';
import { ChildStatusPill } from './EpicChildItems';
import { workTypeUIMap } from '../Dropdowns/TaskTypeDropdown';
import { ModalFooter } from 'components/common/ModalFooter';
import { useAddEpicChildrenMutation, useLinkableEpicItemsQuery } from 'service/project/hooks/useEpicQueries';

const SEARCH_DEBOUNCE_MS = 300;

interface LinkEpicChildrenModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectUuid: string;
  epicUuid: string;
}

/** Links existing Stories, Tasks and Bugs that have no parent yet to the Epic. */
const LinkEpicChildrenModal: React.FC<LinkEpicChildrenModalProps> = ({ isOpen, onClose, projectUuid, epicUuid }) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setDebouncedSearch('');
    setSelected(new Set());
  }, [isOpen]);

  const { data: items = [], isLoading, isFetching } = useLinkableEpicItemsQuery(
    projectUuid,
    epicUuid,
    debouncedSearch,
    isOpen,
  );
  const addChildren = useAddEpicChildrenMutation();

  const toggle = (workItemUuid: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(workItemUuid)) next.delete(workItemUuid);
      else next.add(workItemUuid);
      return next;
    });

  const handleLink = async () => {
    if (!selected.size) return;
    try {
      await addChildren.mutateAsync({ projectUuid, epicUuid, workItemUuids: Array.from(selected) });
      onClose();
    } catch {
      // The toast says why; nothing was linked, so the selection stays for another try.
    }
  };

  return (
    <SprintModal isOpen={isOpen} onClose={onClose} title="Link existing work items" className="!max-w-[600px]">
      <div className="flex flex-col gap-4 px-6 py-5">
        <p className="text-sm text-neutral-medium">
          Stories, Tasks and Bugs that aren’t in an Epic yet. Linking keeps their sprint and status.
        </p>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-medium" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or key"
            className="h-10 w-full rounded-lg border border-primary-subtle bg-primary-variant pl-9 pr-3 text-sm text-neutral outline-none focus:border-[#8B5CF6]"
          />
        </div>

        <div className={`max-h-[320px] overflow-y-auto rounded-lg border border-primary-subtle custom-scrollbar ${isFetching ? 'opacity-70' : ''}`}>
          {isLoading ? (
            <p className="px-4 py-6 text-center text-sm text-neutral-medium">Loading…</p>
          ) : items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-neutral-medium">
              {debouncedSearch ? 'No matching work items.' : 'Every Story, Task and Bug already has a parent.'}
            </p>
          ) : (
            items.map((item) => {
              const type = workTypeUIMap[item.workTypeCategory];
              return (
                <label
                  key={item.workItemUuid}
                  className="flex cursor-pointer items-center gap-3 border-b border-primary-subtle px-4 py-2.5 text-sm last:border-b-0 hover:bg-primary-variant"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(item.workItemUuid)}
                    onChange={() => toggle(item.workItemUuid)}
                    className="h-4 w-4 shrink-0 accent-[#8B5CF6]"
                  />
                  {type && <span className={`${type.color} shrink-0`}>{type.icon}</span>}
                  <span className="shrink-0 font-medium text-neutral">{item.workItemKey}</span>
                  <span className="min-w-0 flex-1 truncate text-neutral-light">{item.title}</span>
                  <ChildStatusPill item={item} />
                </label>
              );
            })
          )}
        </div>

        <ModalFooter
          onCancel={onClose}
          primaryText={selected.size ? `Link ${selected.size}` : 'Link'}
          primaryType="button"
          onPrimary={handleLink}
          isSaving={addChildren.isPending}
        />
      </div>
    </SprintModal>
  );
};

export default LinkEpicChildrenModal;
